import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import useWorkout from '../hooks/useWorkout'
import { formatVideoUrl } from '../utils/video'
import { buildSteps, countWorkSteps, formatClock } from '../utils/workout'

const SOUND_STORAGE_KEY = 'workoutPlayerSound'

// A step runs on a clock if it's a rest, or a work step set up as timed.
const isTimedStep = (step) =>
  !!step && (step.type === 'rest' || step.mode === 'timed')

function WorkoutPlayer() {
  const { id } = useParams()
  const { workout, exercisesById, loading, error, legacyExerciseId } = useWorkout(id)

  const steps = useMemo(
    () => (workout ? buildSteps(workout, exercisesById) : []),
    [workout, exercisesById]
  )

  const [started, setStarted] = useState(false)
  const [finished, setFinished] = useState(false)
  const [paused, setPaused] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [remaining, setRemaining] = useState(0)
  const [soundOn, setSoundOn] = useState(() => {
    if (typeof window === 'undefined') return true
    return window.localStorage.getItem(SOUND_STORAGE_KEY) !== 'off'
  })

  // Mirrors of state the interval callback needs without being re-created.
  const stepsRef = useRef(steps)
  const stepIndexRef = useRef(0)
  const remainingRef = useRef(0)
  const deadlineRef = useRef(null)
  const lastCountdownBeepRef = useRef(null)
  const audioCtxRef = useRef(null)
  const soundOnRef = useRef(soundOn)
  const wakeLockRef = useRef(null)

  useEffect(() => { stepsRef.current = steps }, [steps])
  useEffect(() => { soundOnRef.current = soundOn }, [soundOn])

  const step = steps[stepIndex] || null
  const totalWorkSteps = useMemo(() => countWorkSteps(steps), [steps])
  const completedWorkSteps = useMemo(
    () => (finished
      ? totalWorkSteps
      : countWorkSteps(steps.slice(0, stepIndex))),
    [steps, stepIndex, finished, totalWorkSteps]
  )

  /* ---------------------------------------------------------------- audio */

  const playTone = useCallback((frequency, duration) => {
    if (!soundOnRef.current) return
    const ctx = audioCtxRef.current
    if (!ctx) return
    try {
      const oscillator = ctx.createOscillator()
      const gain = ctx.createGain()
      oscillator.type = 'sine'
      oscillator.frequency.value = frequency
      gain.gain.setValueAtTime(0.0001, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration)
      oscillator.connect(gain)
      gain.connect(ctx.destination)
      oscillator.start()
      oscillator.stop(ctx.currentTime + duration)
    } catch (err) {
      // Audio is a nicety; never let it break the workout.
      console.warn('Could not play tone:', err)
    }
  }, [])

  /* ------------------------------------------------------------ step flow */

  const goTo = useCallback((index) => {
    const total = stepsRef.current.length
    if (index >= total) {
      setFinished(true)
      return
    }
    const safeIndex = Math.max(0, index)
    stepIndexRef.current = safeIndex
    deadlineRef.current = null
    lastCountdownBeepRef.current = null
    setFinished(false)
    setStepIndex(safeIndex)
  }, [])

  const advance = useCallback(() => goTo(stepIndexRef.current + 1), [goTo])
  const rewind = useCallback(() => goTo(stepIndexRef.current - 1), [goTo])

  // Puts the clock back to the full duration of whichever step is at `index`.
  const resetClockFor = useCallback((index) => {
    const current = stepsRef.current[index]
    const seconds = isTimedStep(current) ? current.seconds : 0
    remainingRef.current = seconds
    deadlineRef.current = null
    lastCountdownBeepRef.current = null
    setRemaining(seconds)
  }, [])

  // Reset the clock whenever the current step changes.
  useEffect(() => {
    resetClockFor(stepIndex)
  }, [stepIndex, steps, resetClockFor])

  // The countdown itself. Driven off a wall-clock deadline so that a throttled
  // background tab doesn't cause the timer to drift behind.
  useEffect(() => {
    if (!started || finished || paused) return
    const current = stepsRef.current[stepIndex]
    if (!isTimedStep(current)) return

    if (deadlineRef.current == null) {
      deadlineRef.current = Date.now() + remainingRef.current * 1000
    }

    const interval = setInterval(() => {
      const left = Math.max(0, Math.round((deadlineRef.current - Date.now()) / 1000))

      if (left !== remainingRef.current) {
        remainingRef.current = left
        setRemaining(left)

        if (left > 0 && left <= 3 && lastCountdownBeepRef.current !== left) {
          lastCountdownBeepRef.current = left
          playTone(880, 0.12)
        }
      }

      if (left <= 0) {
        clearInterval(interval)
        playTone(1320, 0.35)
        advance()
      }
    }, 200)

    return () => clearInterval(interval)
  }, [started, finished, paused, stepIndex, advance, playTone])

  /* -------------------------------------------------------- screen wakelock */

  const requestWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) return
    try {
      wakeLockRef.current = await navigator.wakeLock.request('screen')
    } catch (err) {
      // Denied or unsupported — the workout still works, the screen may dim.
      console.warn('Screen wake lock unavailable:', err)
    }
  }, [])

  useEffect(() => {
    if (!started || finished) return

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') requestWakeLock()
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [started, finished, requestWakeLock])

  // Release the lock (and the audio context) when leaving the player.
  useEffect(() => () => {
    wakeLockRef.current?.release?.().catch(() => {})
    wakeLockRef.current = null
    audioCtxRef.current?.close?.().catch(() => {})
    audioCtxRef.current = null
  }, [])

  /* --------------------------------------------------------------- controls */

  const start = useCallback(() => {
    // Both of these need a user gesture, so they have to happen here.
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (Ctx && !audioCtxRef.current) audioCtxRef.current = new Ctx()
    audioCtxRef.current?.resume?.().catch(() => {})
    requestWakeLock()

    stepIndexRef.current = 0
    // Reset explicitly: if we're already sitting on step 0 the effect above
    // won't re-fire, which would otherwise leave a spent clock on screen.
    resetClockFor(0)
    setStepIndex(0)
    setFinished(false)
    setPaused(false)
    setStarted(true)
  }, [requestWakeLock, resetClockFor])

  const togglePause = useCallback(() => {
    setPaused(prev => {
      // Drop the deadline so resuming counts from whatever is left.
      if (!prev) deadlineRef.current = null
      return !prev
    })
  }, [])

  const toggleSound = useCallback(() => {
    setSoundOn(prev => {
      const next = !prev
      window.localStorage.setItem(SOUND_STORAGE_KEY, next ? 'on' : 'off')
      return next
    })
  }, [])

  const restart = useCallback(() => {
    stepIndexRef.current = 0
    resetClockFor(0)
    setStepIndex(0)
    setFinished(false)
    setPaused(false)
    setStarted(true)
  }, [resetClockFor])

  // Keyboard shortcuts for anyone following along on a laptop.
  useEffect(() => {
    if (!started || finished) return

    const handleKey = (event) => {
      if (event.target.matches?.('input, textarea, select')) return

      if (event.code === 'Space') {
        event.preventDefault()
        const current = stepsRef.current[stepIndexRef.current]
        // Space pauses a running clock, but completes a rep-based set.
        if (isTimedStep(current)) togglePause()
        else advance()
      } else if (event.code === 'ArrowRight') {
        event.preventDefault()
        advance()
      } else if (event.code === 'ArrowLeft') {
        event.preventDefault()
        rewind()
      }
    }

    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [started, finished, togglePause, advance, rewind])

  /* ----------------------------------------------------------------- render */

  if (legacyExerciseId) {
    return <Navigate to={`/exercises/${legacyExerciseId}`} replace />
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 pt-32 flex justify-center">
        <div className="text-xl">Loading workout...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-16 pt-32">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error}</p>
          <Link to="/workouts" className="underline mt-2 inline-block">
            Back to all workouts
          </Link>
        </div>
      </div>
    )
  }

  if (steps.length === 0) {
    return (
      <div className="container mx-auto px-4 py-16 pt-32 text-center">
        <h1 className="text-3xl font-bold mb-4">{workout.title}</h1>
        <p className="text-gray-600 mb-6">This workout doesn't have any exercises yet.</p>
        <Link to="/workouts" className="text-indigo-600 hover:text-indigo-800 underline">
          Back to all workouts
        </Link>
      </div>
    )
  }

  /* Pre-start screen */
  if (!started) {
    return (
      <div className="container mx-auto px-4 py-16 pt-32">
        <div className="max-w-xl mx-auto text-center">
          <p className="uppercase tracking-widest text-sm text-gray-500 mb-2">Ready to go</p>
          <h1 className="text-4xl font-bold mb-4">{workout.title}</h1>
          <p className="text-gray-600 mb-8">
            {totalWorkSteps} sets across {workout.items.length} exercises.
            Timed sets advance on their own; for rep-based sets, tap <strong>Done</strong> when
            you finish.
          </p>
          <button
            onClick={start}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xl py-5 px-16 rounded-full transition duration-200"
          >
            Start
          </button>
          <div className="mt-8">
            <Link to={`/workouts/${workout.id}`} className="text-indigo-600 hover:text-indigo-800 underline">
              View the full plan first
            </Link>
          </div>
        </div>
      </div>
    )
  }

  /* Completion screen */
  if (finished) {
    return (
      <div className="container mx-auto px-4 py-16 pt-32">
        <div className="max-w-xl mx-auto text-center">
          <div className="text-6xl mb-4" role="img" aria-label="Celebration">🎉</div>
          <h1 className="text-4xl font-bold mb-4">Workout complete</h1>
          <p className="text-gray-600 mb-8">
            Nice work — you finished all {totalWorkSteps} sets of {workout.title}.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={restart}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-8 rounded-lg transition duration-200"
            >
              Go again
            </button>
            <Link
              to="/workouts"
              className="border border-indigo-600 text-indigo-600 hover:bg-indigo-50 font-bold py-3 px-8 rounded-lg transition duration-200"
            >
              More workouts
            </Link>
          </div>
          <div className="bg-indigo-100 rounded-lg p-6 mt-12 text-left">
            <h3 className="text-xl font-bold text-indigo-800 mb-2">Ready for the next level?</h3>
            <p className="text-indigo-700 mb-4">
              Book a consultation with Alex for a plan built around your goals.
            </p>
            <Link
              to="/book-consultation"
              className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
            >
              Book a Consultation
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const isRest = step.type === 'rest'
  const nextStep = steps[stepIndex + 1] || null
  // During a rest we preview the exercise that's coming up next.
  const shownExercise = isRest ? (nextStep?.exercise || null) : step.exercise
  const videoSrc = shownExercise?.videoUrl
    ? formatVideoUrl(shownExercise.videoUrl, { autoplay: true, loop: true })
    : null
  const progressPercent = totalWorkSteps
    ? Math.round((completedWorkSteps / totalWorkSteps) * 100)
    : 0
  const isLastStep = stepIndex === steps.length - 1

  return (
    <div className="bg-gray-900 min-h-screen pt-24 pb-12 text-white">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-400 mb-2">
            <span>{workout.title}</span>
            <span>{Math.min(completedWorkSteps + (isRest ? 0 : 1), totalWorkSteps)} of {totalWorkSteps} sets</span>
          </div>
          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-500 transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Video */}
        {videoSrc && (
          <div className="aspect-w-16 aspect-h-9 bg-black rounded-lg overflow-hidden mb-6">
            <iframe
              key={shownExercise.id}
              src={videoSrc}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={`${shownExercise.title} video`}
            ></iframe>
          </div>
        )}

        {/* Current step */}
        <div className={`rounded-lg p-8 text-center mb-6 ${isRest ? 'bg-emerald-900' : 'bg-gray-800'}`}>
          {isRest ? (
            <>
              <p className="uppercase tracking-widest text-sm text-emerald-300 mb-2">Rest</p>
              <div className="text-7xl font-bold tabular-nums mb-3">{formatClock(remaining)}</div>
              <p className="text-emerald-100">
                {nextStep
                  ? (step.nextIsSameExercise
                      ? `Next: set ${nextStep.setIndex + 1} of ${nextStep.setCount} — ${nextStep.title}`
                      : `Up next: ${nextStep.title}`)
                  : 'Last one done'}
              </p>
            </>
          ) : (
            <>
              <p className="uppercase tracking-widest text-sm text-indigo-300 mb-2">
                Set {step.setIndex + 1} of {step.setCount}
              </p>
              <h1 className="text-3xl font-bold mb-4">{step.title}</h1>
              {step.mode === 'timed' ? (
                <div className="text-7xl font-bold tabular-nums mb-3">{formatClock(remaining)}</div>
              ) : (
                <div className="text-6xl font-bold mb-3">{step.reps} reps</div>
              )}
              {step.notes && <p className="text-gray-300 italic">{step.notes}</p>}
              {step.exercise && (
                <Link
                  to={`/exercises/${step.exercise.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-300 hover:text-indigo-200 text-sm underline inline-block mt-3"
                >
                  How to do this exercise
                </Link>
              )}
            </>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <button
            onClick={rewind}
            disabled={stepIndex === 0}
            className="bg-gray-700 hover:bg-gray-600 disabled:opacity-40 disabled:hover:bg-gray-700 text-white font-medium py-3 px-5 rounded-lg transition duration-200"
          >
            Back
          </button>

          {isTimedStep(step) && (
            <button
              onClick={togglePause}
              className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-3 px-5 rounded-lg transition duration-200"
            >
              {paused ? 'Resume' : 'Pause'}
            </button>
          )}

          <button
            onClick={advance}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-8 rounded-lg transition duration-200"
          >
            {isLastStep ? 'Finish' : isRest ? 'Skip rest' : step.mode === 'timed' ? 'Skip' : 'Done'}
          </button>
        </div>

        {paused && (
          <p className="text-center text-yellow-400 mb-4">Paused</p>
        )}

        {/* Up next list */}
        {nextStep && (
          <p className="text-center text-gray-400 text-sm mb-6">
            Up next: {nextStep.type === 'rest'
              ? `${formatClock(nextStep.seconds)} rest`
              : `${nextStep.title} — set ${nextStep.setIndex + 1} of ${nextStep.setCount}`}
          </p>
        )}

        <div className="flex justify-between items-center text-sm text-gray-500 border-t border-gray-800 pt-4">
          <Link to={`/workouts/${workout.id}`} className="hover:text-gray-300 underline">
            Exit workout
          </Link>
          <button onClick={toggleSound} className="hover:text-gray-300">
            Sound: {soundOn ? 'on' : 'off'}
          </button>
        </div>

        <p className="text-center text-xs text-gray-600 mt-4 hidden md:block">
          Space to {isTimedStep(step) ? 'pause' : 'complete a set'} &middot; arrow keys to move between steps
        </p>
      </div>
    </div>
  )
}

export default WorkoutPlayer
